import { ITimeEntry, ITimeEntryFilter, ITimeEntryFilterCriteria, ITimeEntryMissingDay } from '../../queries/TimeEntry';
import { formatDayShort } from '../../timeTracking/days';

export const ALL = 'all';

export interface IReviewFilters {
	rep: string;
	office: string;
}

export function matchesReviewFilters(entry: ITimeEntry, { rep, office }: IReviewFilters) {
	return (rep === ALL || entry.userId === rep) && (office === ALL || entry.officeLocation === office);
}

/** An admin's All entries request: their rep and manager picks go to the API, which matches the manager frozen on each entry. */
export function buildEntriesFilter({ fromDay, rep, manager }: { fromDay: string; rep: string; manager: string }) {
	const criteria: ITimeEntryFilterCriteria[] = [{ op: 'Gt', property: 'Day', value: fromDay }];
	if (rep !== ALL) {
		criteria.push({ property: 'User', value: rep });
	}
	if (manager !== ALL) {
		criteria.push({ property: 'Manager', value: manager });
	}
	return { criteria } satisfies ITimeEntryFilter;
}

/** An admin's queue options: people with days waiting, narrowed to the picked manager's reps. */
export function buildQueueOptionsFilter({ manager }: { manager: string }) {
	const criteria: ITimeEntryFilterCriteria[] = [{ property: 'Status', value: 'Submitted' }];
	if (manager !== ALL) {
		criteria.push({ property: 'Manager', value: manager });
	}
	return { criteria } satisfies ITimeEntryFilter;
}

/** An admin in the MGR role starts on their own reports until they pick otherwise; other admins start on every manager. */
export function defaultManagerFilter({ isManager, userId }: { isManager: boolean; userId?: string }) {
	return isManager && userId ? userId : ALL;
}

export function distinctOffices(entries: ITimeEntry[]) {
	return [...new Set(entries.map(entry => entry.officeLocation).filter((office): office is string => !!office))].sort();
}

export function missingDayChip({ day, status }: ITimeEntryMissingDay) {
	const label = formatDayShort(day);
	switch (status) {
		case 'Draft':
			return { color: 'orange', label: `${label} · draft` } as const;
		case 'Rejected':
			return { color: 'red', label: `${label} · sent back` } as const;
		default:
			return { color: 'gray', label } as const;
	}
}

export const MAX_MISSING_CHIPS = 10;

export function capMissingDays(days: ITimeEntryMissingDay[], max = MAX_MISSING_CHIPS) {
	return { hiddenCount: Math.max(days.length - max, 0), shown: days.slice(0, max) };
}

export const OVER_SPAN_WARNING_MINUTES = 15;
